import api from "./api";

// Coach dashboard data layer — wraps the existing backend endpoints
// (see SFMIS-Backend/routes/web.php). Some endpoints wrap their single result
// in an outer array ([[ ... ]]); those are unwrapped here so callers get a
// plain object.

// GET /api/coaches/{coachId}/club-verification-requests → array of pending requests
export const getClubVerificationRequests = (coachId) =>
  api.get(`/api/coaches/${coachId}/club-verification-requests`).then((r) => r.data);

// POST /api/club-verification-requests/{processId}/approve { coach_id }
// The route is now keyed by the registration PROCESS id (from a pending
// request's `process_id`), not the member id. The body still carries coach_id.
export const approveClubRequest = (processId, coachId) =>
  api
    .post(`/api/club-verification-requests/${processId}/approve`, { coach_id: coachId })
    .then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// POST /api/club-verification-requests/{processId}/reject { coach_id }
export const rejectClubRequest = (processId, coachId) =>
  api
    .post(`/api/club-verification-requests/${processId}/reject`, { coach_id: coachId })
    .then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// GET /api/clubs/{clubId}/members → array of this club's own members
// (ClubTeamController::members), gated by "view_own_club_members" + the
// `club.coach` middleware (EnsureClubCoach — any coach whose own club_id
// matches {clubId}). Returns {id, title, initials, name_denoted_by_initials,
// lastname, email, photo_path, photo_url, member_type_id, member_type,
// member_status_id, member_status, club_id, member_code, club_code} per row
// — no query-string filtering support, so callers narrow client-side.
//
// NOT the same as /api/members?club_id=… (MemberController::index) — that
// route is gated by "view_members", a system-user-only permission no coach
// role is ever granted, so it 403s for a coach's own token. This function
// used to call that route (silently 403ing, always resolving to []) before
// being pointed at the correctly-permissioned club-scoped route instead.
export const getClubMembers = (clubId) => api.get(`/api/clubs/${clubId}/members`).then((r) => r.data);

// GET /api/member-types → array of Member Type categories ({category_id, description}).
// Used to resolve the "Coach" type id so coordinators can be fetched by type.
export const getMemberTypes = () => api.get(`/api/member-types`).then((r) => r.data);

// GET /api/facilities → array of facilities (with slot_count + booking_fee)
export const getFacilities = () => api.get(`/api/facilities`).then((r) => r.data);

// GET /api/facilities/{id}/availability?date=YYYY-MM-DD → availability object
// (the endpoint returns [[ {…} ]], so unwrap the first element).
export const getFacilityAvailability = (facilityId, date) =>
  api
    .get(`/api/facilities/${facilityId}/availability`, { params: { date } })
    .then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// GET /api/clubs/{id} → club detail object (the endpoint returns [[ {…} ]],
// so unwrap the first element).
export const getClub = (clubId) =>
  api.get(`/api/clubs/${clubId}`).then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// GET /api/payments?type=booking&club_id=… → this club's booking payments.
// Same "backend ignores club_id" situation as getClubRegistrationPayments
// below: PaymentController::index() never reads the club_id query param (it
// only reads search/payer_id/payment_status_id/type), so without the
// client-side re-filter this returns EVERY booking payment system-wide, from
// any club. `type=booking` IS a real, backend-supported filter, though — it
// keeps the merged member_registration/club_registration/renewal/reprint
// rows (which don't have a payer_id at all) out of what's meant to be a
// booking-only table. Each surviving row's own `club_id` (the payer's club)
// is what the re-filter matches against.
export const getClubPayments = (clubId) =>
  api
    .get(`/api/payments`, { params: { type: "booking", club_id: clubId } })
    .then((r) => (Array.isArray(r.data) ? r.data.filter((p) => p.club_id === clubId) : []));

// GET /api/registration-payments/clubs?club_id=… → this club's registration-fee
// payment history. NOTE: as of this writing the backend route
// (RegistrationPaymentController::clubPayments) is the admin-wide list — it
// ignores club_id and is gated by permission:view_payments, which coach roles
// aren't granted — so this 403s for a real coach token until the backend adds
// self-scoping. The club_id filter (and the client-side re-filter below) are
// here so this starts working the moment that lands, without another
// frontend change.
export const getClubRegistrationPayments = (clubId) =>
  api
    .get(`/api/registration-payments/clubs`, { params: { club_id: clubId } })
    .then((r) => (Array.isArray(r.data) ? r.data.filter((p) => p.club_id === clubId) : []));

// POST /api/club-registration-processes/{processId}/simulate-payment-success →
// test payment gateway for the CLUB registration fee. Given a club registration
// PROCESS id, marks its pending registration-fee payment as paid and activates
// the club + its coaches. Optionally pass the amount paid (defaults server-side
// to the process's payable amount). Returns the updated process/payment summary.
export const simulateClubPaymentSuccess = (processId, paidAmount) =>
  api
    .post(
      `/api/club-registration-processes/${processId}/simulate-payment-success`,
      paidAmount != null ? { paid_amount: paidAmount } : {}
    )
    .then((r) => r.data);

// GET /api/attendances?date=… → array of attendance scans. The backend has no
// club filter, so callers narrow to their club members client-side.
export const getAttendances = (params = {}) =>
  api.get(`/api/attendances`, { params }).then((r) => r.data);

// POST /api/clubs/{clubId}/coaches — the club's own primary coach adds a
// coach to their club (ClubTeamController::addCoach), gated by
// "add_own_club_coaches" + the `primary.coach` middleware (member/coach
// token only, and it must belong to that club's own primary coach — same
// gate as club renewal). `formData` is either a brand-new coach's full
// profile (title, initials, name_denoted_by_initials, lastname,
// member_gender_id, email, primary_phone_number, address required;
// nic_number, secondary_phone_number, date_of_birth, photo optional) or an
// existing coach attached by `email` alone — the backend resolves that case
// from its own stored profile and ignores any other submitted fields.
// Creates a member_registration_process (fee = "Additional Coach Fee") and,
// unlike club renewal, already returns a real payment_payload — so it's paid
// the same way as registration (card via cyberGatewayService, or
// pay-at-counter with the existing member applicant-shroff-voucher route).
export const addClubCoach = (clubId, formData) =>
  api.post(`/api/clubs/${clubId}/coaches`, formData).then((r) => r.data);

// DELETE /api/clubs/{clubId}/coaches/{coachId} — same gating as addClubCoach.
// 422s if coachId is the club's own primary coach (can't remove yourself this
// way — transfer primary status first). Demotes the coach to Independent if
// they're not a coach of any other club, otherwise just detaches them from
// this one.
export const removeClubCoach = (clubId, coachId) =>
  api.delete(`/api/clubs/${clubId}/coaches/${coachId}`).then((r) => r.data);
